/**
 * @fileoverview System-web module catalog service — local-first reads/writes
 *               for the platform `modules` collection.
 * @module system-web/services/moduleService
 *
 * Responsibilities:
 * - Read the synced `modules` Dexie table (listModules, getModule).
 * - Normalize catalog docs into selector options (listModuleOptions).
 * - Write module CRUD + JSON seed imports to Dexie with sync wakeup.
 *
 * Dependencies:
 * - @firesquirrel/local-db (getSystemDb, wakeLocalWrite)
 * - @firesquirrel/core-domain (toModuleOptions — shared selector normalizer)
 * - ./systemAuditLogService (audit trail for module mutations)
 *
 * Architecture:
 * - Local-first: all reads/writes hit Dexie; background sync reconciles with
 *   the system DB `modules` collection (the source of truth).
 * - `listModuleOptions` is the single entrypoint for module SELECTORS
 *   (lookup categories, lookup values). Catalog management surfaces use
 *   `listModules` directly.
 *
 * Notes:
 * - Legacy cleanup (sanitizeModuleDoc, isLegacyRemovedModule) keeps
 *   pre-catalog records from surfacing in any reader.
 */

import { getSystemDb, wakeLocalWrite } from '@firesquirrel/local-db';
import { toModuleOptions, type ModuleOption } from '@firesquirrel/core-domain';
import { writeAuditLog } from './systemAuditLogService';

export function generateModuleCode(name: string): string {
  return String(name ?? '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');
}

function sanitizeModuleDoc(doc: any): any {
  if (!doc) return doc;

  const { minTier, version, dependencies, icon, sortOrder, ...rest } = doc;
  return {
    ...rest,
    code: String(rest.code ?? '').trim(),
    name: String(rest.name ?? '').trim(),
    description: String(rest.description ?? '').trim() || undefined,
    category: rest.category ?? 'inspection',
    status: rest.status ?? 'alpha',
    enabledByDefault: Boolean(rest.enabledByDefault ?? false),
  };
}

function normalizeModuleInput(data: any, existing?: any) {
  const now = new Date().toISOString();
  const code = String(data?.code || '').trim() || generateModuleCode(data?.name);

  return {
    _id: data?._id ?? existing?._id ?? `local-${Date.now()}`,
    code,
    name: String(data?.name || existing?.name || '').trim(),
    description: String(data?.description ?? existing?.description ?? '').trim() || undefined,
    category: data?.category ?? existing?.category ?? 'inspection',
    status: data?.status ?? existing?.status ?? 'alpha',
    enabledByDefault: Boolean(data?.enabledByDefault ?? existing?.enabledByDefault ?? false),
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
  };
}

function hasLegacyModuleFields(doc: any): boolean {
  return ['minTier', 'version', 'dependencies', 'icon', 'sortOrder']
    .some((field) => Object.prototype.hasOwnProperty.call(doc ?? {}, field));
}

function isLegacyRemovedModule(doc: any): boolean {
  const code = String(doc?.code ?? '').trim().toLowerCase();
  const name = String(doc?.name ?? '').trim().toLowerCase();
  return code === 'firecode' || code === 'fire-code' || name === 'fire code';
}

function isMongoId(id: string | undefined | null): boolean {
  return typeof id === 'string' && /^[0-9a-f]{24}$/i.test(id);
}

function toSeedModuleRecord(doc: any): {
  code: string;
  name: string;
  description?: string;
  category: string;
  status: string;
  enabledByDefault: boolean;
} {
  const sanitized = sanitizeModuleDoc(doc);
  return {
    code: sanitized.code,
    name: sanitized.name,
    ...(sanitized.description ? { description: sanitized.description } : {}),
    category: sanitized.category,
    status: sanitized.status,
    enabledByDefault: Boolean(sanitized.enabledByDefault),
  };
}

export function formatModuleSeedJson(modules: any[]): string {
  return JSON.stringify(modules.map((module) => toSeedModuleRecord(module)), null, 2);
}

function parseModuleSeedJson(seedJson: string): ReturnType<typeof toSeedModuleRecord>[] {
  const parsed = JSON.parse(seedJson);
  if (!Array.isArray(parsed)) {
    throw new Error('Seed JSON must be an array of modules');
  }
  return parsed.map((module: any) => {
    if (!module || typeof module !== 'object') {
      throw new Error('Seed JSON contains an invalid module entry');
    }
    const code = String(module.code ?? '').trim();
    const name = String(module.name ?? '').trim();
    const category = String(module.category ?? '').trim();
    const status = String(module.status ?? '').trim();
    if (!code || !name || !category || !status) {
      throw new Error('Each module must include code, name, category, and status');
    }
    return toSeedModuleRecord(module);
  });
}

async function replaceLocalModules(modules: any[]): Promise<void> {
  const db = await getSystemDb();
  const current = await db.modules.toArray();
  if (modules.length > 0) {
    for (const doc of modules.map((item) => sanitizeModuleDoc(item))) {
      const existing = (current as any[]).find((item) => String(item?.code ?? '') === doc.code);
      await db.modules.put({
        ...doc,
        _id: existing?._id ?? doc._id ?? `local-${Date.now()}-${doc.code}`,
        createdAt: existing?.createdAt ?? doc.createdAt ?? new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
    }
  }
  wakeLocalWrite('modules');
}

async function seedModulesPayload(modules: any[]): Promise<number> {
  const payload = modules.map((module) => toSeedModuleRecord(module));

  await replaceLocalModules(payload);
  return payload.length;
}

async function cleanupLegacyModuleDocs(): Promise<void> {
  const db = await getSystemDb();
  const docs = await db.modules.toArray();
  let changed = false;

  for (const doc of docs as any[]) {
    if (!hasLegacyModuleFields(doc)) {
      continue;
    }

    const cleaned = sanitizeModuleDoc(doc);
    if (JSON.stringify(cleaned) !== JSON.stringify(doc)) {
      await db.modules.put(cleaned);
      changed = true;
    }
  }

  if (changed) {
    wakeLocalWrite('modules');
  }
}

export async function listModules(params?: { status?: string; search?: string }): Promise<any[]> {
  const db = await getSystemDb();
  await cleanupLegacyModuleDocs();
  const docs = await db.modules.toArray();
  let results = (docs as any[])
    .map((doc) => sanitizeModuleDoc(doc))
    .filter((doc) => !isLegacyRemovedModule(doc));
  if (params?.status) results = results.filter(m => m.status === params.status);
  if (params?.search) {
    const q = params.search.toLowerCase();
    results = results.filter(m => m.code?.toLowerCase().includes(q) || m.name?.toLowerCase().includes(q));
  }
  return results;
}

/**
 * Selector options for the real modules catalog — the single source for any
 * UI where a module is selectable (lookup categories, lookup values).
 * Returns `{ key: code, label: name }` options via the shared
 * `toModuleOptions` normalizer so this list can never drift from the catalog.
 */
export async function listModuleOptions(): Promise<ModuleOption[]> {
  return toModuleOptions(await listModules());
}

export async function getModule(codeOrId: string): Promise<any | null> {
  const db = await getSystemDb();
  let doc = await db.modules.get(codeOrId);
  if (!doc) {
    const all = await listModules();
    const match = all.find((m: any) => m.code === codeOrId);
    return match ?? null;
  }
  const cleaned = sanitizeModuleDoc(doc);
  if (JSON.stringify(cleaned) !== JSON.stringify(doc)) {
    await db.modules.put(cleaned);
    wakeLocalWrite('modules');
  }
  return cleaned;
}

export async function upsertModule(data: any): Promise<void> {
  const db = await getSystemDb();
  const existing = await db.modules.get(data._id);
  const isNew = !existing;
  const next = normalizeModuleInput(data, existing);
  await db.modules.put(next);
  wakeLocalWrite('modules');
  writeAuditLog({
    collection: 'modules',
    documentId: next._id,
    action: isNew ? 'create' : 'update',
    summary: isNew ? `Created module "${next.code}"` : `Updated module "${next.code}"`,
  }).catch((err) => {
    console.warn('[moduleService] Failed to write audit log for module save:', err);
  });
}

export async function deleteModule(id: string): Promise<void> {
  const db = await getSystemDb();
  const existing = await db.modules.get(id);
  const code = existing ? (existing as any).code ?? id : id;

  if (existing) {
    await db.modules.put({
      ...(existing as any),
      _deleted: true,
      deletedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
    wakeLocalWrite('modules');
  }

  writeAuditLog({
    collection: 'modules',
    documentId: id,
    action: 'delete',
    summary: `Deleted module "${code}"`,
  }).catch((err) => {
    console.warn('[moduleService] Failed to write audit log for module delete:', err);
  });
}

export async function seedModulesFromCurrentCatalog(): Promise<number> {
  const db = await getSystemDb();
  const current = await db.modules.toArray();
  return seedModulesPayload(current);
}

export async function seedModulesFromJson(seedJson: string): Promise<number> {
  const modules = parseModuleSeedJson(seedJson);
  return seedModulesPayload(modules);
}
